import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import AccessDeviceIcons from './AccessDeviceIcons';
import ExperienceRating from './ExperienceRating';

const ExperienceOrbStyled = styled(motion.div)`
  width: 180px;
  height: 180px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.02) 70%);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.15);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  text-align: center;
  transition: all 0.4s ease;
  box-shadow: ${props => {
    const colors = {
      'Featured': '#ff00aa',
      '360°': '#00f5ff',
      'AR': '#45B7D1',
      'AR Portal': '#00aaff',
      'AR Performance': '#00ff88',
      'AR Art': '#ff66ff',
      'AR Character': '#ff0066',
      'AR Game': '#ffee00',
      'AR Location': '#00ff66',
      'LBE': '#e85d04',
      'Location-Based': '#e85d04',
      'Metaverse': '#9d4edd',
      'Virtual Reality': '#2196F3'
    };
    const color = colors[props.$category] || '#00f5ff';
    return `0 0 20px ${color}30, inset 0 0 30px ${color}10`;
  }};

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: ${props => {
      const colors = {
        'Featured': '#ff00aa',
        '360°': '#00f5ff',
        'AR': '#45B7D1',
        'AR Portal': '#00aaff',
        'AR Performance': '#00ff88',
        'AR Art': '#ff66ff',
        'AR Character': '#ff0066',
        'AR Game': '#ffee00',
        'AR Location': '#00ff66',
      'Location-Based': '#e85d04',
      'Metaverse': '#9d4edd',
      'Virtual Reality': '#2196F3'
      };
      const color = colors[props.$category] || '#00f5ff';
      return `conic-gradient(from 0deg, ${color}40, transparent 40%, transparent 60%, ${color}40)`;
    }};
    animation: rotate 15s linear infinite;
    opacity: 0.6;
    pointer-events: none;
  }

  @keyframes rotate {
    to { transform: rotate(360deg); }
  }

  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
    box-shadow: ${props => {
      const colors = {
        'Featured': '#ff00aa',
        '360°': '#00f5ff',
        'AR': '#45B7D1',
        'AR Portal': '#00aaff',
        'AR Performance': '#00ff88',
        'AR Art': '#ff66ff',
        'AR Character': '#ff0066',
        'AR Game': '#ffee00',
        'AR Location': '#00ff66',
        'Location-Based': '#e85d04',
        'Metaverse': '#9d4edd',
        'Virtual Reality': '#2196F3'
      };
      const color = colors[props.$category] || '#00f5ff';
      return `0 0 40px ${color}50, 0 0 60px ${color}30, inset 0 0 40px ${color}20`;
    }};
  }

  &:hover::before {
    animation-duration: 3s;
    opacity: 0.9;
  }
`;

const OrbContent = styled.div`
  position: relative;
  z-index: 2;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 15px;
`;

const SaveButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 3;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${props => props.$filled ? '#ff00aa' : 'rgba(255,255,255,0.8)'};
  font-size: 1rem;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`;

const OrbThumbnail = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: url(${props => props.src}) center/cover;
  margin-bottom: 10px;
`;

const OrbTitle = styled.h3`
  font-size: 0.85rem;
  font-weight: 600;
  margin: 0;
  margin-bottom: 5px;
  line-height: 1.2;
`;

const OrbCategory = styled.span`
  background: ${props => {
    const colors = {
      'Featured': '#ff00aa',
      '360°': '#00f5ff',
      'AR': '#45B7D1',
      'AR Portal': '#00aaff',
      'AR Performance': '#00ff88',
      'AR Art': '#ff66ff',
      'AR Character': '#ff0066',
      'AR Game': '#ffee00',
      'AR Location': '#00ff66',
      'LBE': '#e85d04',
      'Location-Based': '#e85d04',
      'Metaverse': '#9d4edd',
      'Virtual Reality': '#2196F3'
    };
    return colors[props.$category] || '#00f5ff';
  }};
  color: ${props => (props.$category === 'AR Game' || props.$category === 'AR') ? '#000' : '#fff'};
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
`;

const orbVariants = {
  hidden: { opacity: 0, scale: 0.8, y: 50 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 100, damping: 15 }
  },
  hover: { scale: 1.1, y: -10, transition: { duration: 0.3 } }
};

function ExperienceOrb({ experience, index = 0, onExperienceClick, showSaveButton, isSaved, onSaveClick }) {
  const handleClick = (e) => {
    if (e.target.closest('button')) return;
    onExperienceClick?.(experience);
  };

  const handleSaveClick = (e) => {
    e.stopPropagation();
    onSaveClick?.(experience.id, e.currentTarget);
  };

  return (
    <ExperienceOrbStyled
      $category={experience.categoryChip}
      variants={orbVariants}
      initial="hidden"
      animate="visible"
      whileHover="hover"
      whileTap={{ scale: 0.95 }}
      transition={{ delay: index * 0.1 }}
      onClick={handleClick}
    >
      <OrbContent>
        {showSaveButton && (
          <SaveButton type="button" $filled={isSaved} onClick={handleSaveClick} aria-label={isSaved ? 'Unsave' : 'Save'}>
            {isSaved ? '★' : '☆'}
          </SaveButton>
        )}
        <OrbThumbnail src={experience.thumbnail} />
        <OrbTitle>{experience.title}</OrbTitle>
        <ExperienceRating experienceId={experience.id} compact />
        <OrbCategory $category={experience.categoryChip}>{experience.categoryChip}</OrbCategory>
        <AccessDeviceIcons experience={experience} compact />
      </OrbContent>
    </ExperienceOrbStyled>
  );
}

export default ExperienceOrb;
